import type { TopupQueueRow } from "@/modules/wallet/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { CompactNumber } from "@/shared/ui/CompactNumber";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  TOPUP_STATUS_BADGE,
  TOPUP_STATUS_LABELS,
} from "./topup-queue-labels";
import {
  formatTopupMethodLabel,
  isPosQrCounterTopup,
} from "./topup-queue-utils";

type Props = {
  row: TopupQueueRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function TopupQueueDetailDialog({ row, open, onOpenChange }: Props) {
  if (!row) return null;

  const proofSrc = row.slipUrl ? resolveImageSrc(row.slipUrl) : null;
  const posQr = isPosQrCounterTopup(row);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ລາຍລະອຽດເຕີມເງິນ</DialogTitle>
        </DialogHeader>

        <dl className="space-y-3 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">ລູກຄ້າ</dt>
            <dd className="text-right font-medium">
              {row.customerName ?? "–"}
              <span className="text-muted-foreground block text-xs font-normal">
                {row.customerPhone}
              </span>
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">ວິທີ</dt>
            <dd>{formatTopupMethodLabel(row)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">ຍອດ</dt>
            <dd>
              <CompactNumber
                value={row.amountLak}
                formatOptions={{
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }}
              />{" "}
              LAK
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">ສະຖານະ</dt>
            <dd>
              <Badge variant={TOPUP_STATUS_BADGE[row.status]}>
                {TOPUP_STATUS_LABELS[row.status]}
              </Badge>
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">ຮ້ອງຂໍເມື່ອ</dt>
            <dd>{formatDateTimeLocal(row.requestedAt)}</dd>
          </div>
          {row.reviewedAt ? (
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">ດຳເນີນການເມື່ອ</dt>
              <dd>{formatDateTimeLocal(row.reviewedAt)}</dd>
            </div>
          ) : null}
          {posQr ? (
            <>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">QR session</dt>
                <dd className="max-w-[220px] truncate font-mono text-xs">
                  {row.sourceSessionId}
                </dd>
              </div>
              {row.walletTransactionId ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Txn wallet</dt>
                  <dd className="max-w-[220px] truncate font-mono text-xs">
                    {row.walletTransactionId}
                  </dd>
                </div>
              ) : null}
              <p className="text-muted-foreground rounded-lg border bg-muted/40 px-3 py-2 text-xs">
                ຢືນຢັນແລະເຕີມເງິນທີ່ POS ແລ້ວ — ບໍ່ຕ້ອງອະນຸມັດຊ້ຳໃນ Admin
              </p>
            </>
          ) : null}
        </dl>

        {proofSrc ? (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium">ຫຼັກຖານ</p>
            <a
              href={proofSrc}
              target="_blank"
              rel="noopener noreferrer"
              className="block overflow-hidden rounded-lg border"
            >
              <img
                src={proofSrc}
                alt="ຫຼັກຖານເຕີມເງິນ"
                className="max-h-72 w-full object-contain bg-muted/30"
              />
            </a>
          </div>
        ) : (
          <p className="text-muted-foreground mt-4 text-sm">ບໍ່ມີຮູບຫຼັກຖານ</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
